import React from "react";
import moment from "moment";
import { ResourcePlanSchema, ResourceSchema } from "../../data/types";
import { getTimeBlockPeriodType } from "../../utils/helpers";

type Moment = moment.Moment;


interface TimeBlock {
  start: Moment;
  end: Moment;
}



type TimesViewProps = {
  availableTimeBlocks: TimeBlock[] | undefined,
  selectedDay: Date
  selectedDatesCount?: number;
  selectedBlock: TimeBlock | undefined
  setSelectedBlock: React.Dispatch<React.SetStateAction<TimeBlock | undefined>>;
  updateEndTime: (startTime: Moment) => void;
  updateStartTime: (startTime: Moment) => void;
  resourceDetail?: ResourceSchema;
  selectedPlan: ResourcePlanSchema;
  timeSelectionError?: string | null;
}

const TimesView: React.FC<TimesViewProps> = ({
  availableTimeBlocks,
  selectedDay,
  selectedDatesCount = 1,
  selectedBlock,
  selectedPlan,
  setSelectedBlock,
  updateStartTime,
  updateEndTime,
  resourceDetail,
  timeSelectionError,
}) => {

  const getPeriodTypeStyle = (periodType: string) => {
    switch (periodType) {
      case 'PEAK':
        return {
          bgColor: 'bg-red-50/40',
          borderColor: 'border-red-300',
          textColor: 'text-red-700',
          badge: 'bg-red-100/80 text-red-800/80'
        };
      case 'OFF_PEAK':
        return {
          bgColor: 'bg-green-50/40',
          borderColor: 'border-green-300',
          textColor: 'text-green-700',
          badge: 'bg-green-100/80 text-green-800/80'
        };
      case 'STANDARD':
      default:
        return {
          bgColor: 'bg-blue-50/40',
          borderColor: 'border-blue-300',
          textColor: 'text-blue-700',
          badge: 'bg-blue-100/80 text-blue-800/80'
        };
    }
  };

  const handleTimeClick = (block: TimeBlock) => {
    setSelectedBlock(block);
    const startTime = moment(selectedDay)
      .clone()
      .hours(block.start.hours())
      .minutes(block.start.minutes());
    const endTime = moment(selectedDay)
      .clone()
      .hours(block.end.hours())
      .minutes(block.end.minutes());
    updateStartTime(startTime);
    updateEndTime(endTime);
  };

  const dateLabel =
    selectedDatesCount > 1
      ? `${selectedDatesCount} selected dates (times shown for ${moment(selectedDay).format("ddd, LL")})`
      : moment(selectedDay).format("ddd, LL");

  return (
    <div className="w-11/12">

      <p className="text-black font-medium text-left text-[16px] font-raleway mt-3">
        Select an available time for <b>{dateLabel}</b>
      </p>

      {timeSelectionError ? (
        <p
          className="mt-2 text-left text-[14px] font-open-sans text-red-600 bg-red-50 border border-red-200 rounded-lg p-3"
          role="alert"
          data-testid="time-selection-error"
        >
          {timeSelectionError}
        </p>
      ) : null}

      <div
        className={`grid sm:grid-cols-2 grid-cols-1 space-y-1 space-x-2 grid-flow-row-dense items-center w-full h-full`}
      >
      {availableTimeBlocks?.map((block, index) => {
        const periodType = getTimeBlockPeriodType(block.start, block.end, resourceDetail?.resource_options);
        const style = getPeriodTypeStyle(periodType);
        const isSelected = moment(block.start).isSame(moment(selectedBlock?.start));
        
        return (
          <div
            key={index}
            className={`${
              isSelected
                ? " bg-primaryDark-100 text-primaryLight-1000"
                : `text-black hover:bg-primaryDark-400 hover:text-white ${(selectedPlan.pricing_type === 'dynamic' && selectedPlan.plan_recurrence === 'hourly') ? `${style.bgColor}  border-[1px] border-[#E9E9E9]/70` : 'bg-white border-[1px] border-[#E9E9E9]'}`
            } font-open-sans text-[16px] border-[1px] p-2 rounded-lg hover:cursor-pointer hover:bg-primary-400/60`}
            onClick={() => handleTimeClick(block)}
          >
            <div className="flex items-center text-sm justify-between">
              <span>{block.start.format("LT")} - {block.end.format("LT")}</span>
              {(!isSelected && (selectedPlan.pricing_type === 'dynamic' && selectedPlan.plan_recurrence === 'hourly')) && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.badge}`}>
                  {periodType === 'PEAK' ? 'Peak' : periodType === 'OFF_PEAK' ? 'Off-Peak' : 'Standard'}
                </span>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  )
}

export default TimesView
